import { defaultSchema } from "rehype-sanitize";
import type { Schema } from "hast-util-sanitize";

/**
 * Sanitization schema for user-provided Markdown.
 *
 * Starts from the well-vetted `defaultSchema` from rehype-sanitize (which
 * already strips <script>, <iframe>, on*-handlers, javascript: URLs, etc.)
 * and adds a few attributes we genuinely need:
 *
 *  - `id` on headings (for in-document anchors / TOC)
 *  - `className` on `<code>` (so language-* hints survive for syntax highlighting)
 *
 * Anything not on this allow-list is dropped — that includes raw HTML, inline
 * event handlers, and javascript: URLs in links/images. Note that this schema
 * governs the *Markdown* tree only: section permalinks are rendered as React
 * components after sanitization (see `MarkdownContent`), precisely because the
 * ARIA attributes they need do not survive a pass through here.
 */
export const sanitizeSchema: Schema = {
  ...defaultSchema,
  // We render into our own root inside a SPA; we own every id on the page, so
  // the default `user-content-` prefix that `rehype-sanitize` adds to ids
  // would only break our table-of-contents anchors.
  clobberPrefix: "",
  attributes: {
    ...defaultSchema.attributes,
    "*": [...(defaultSchema.attributes?.["*"] ?? []), "className"],
    code: [...(defaultSchema.attributes?.code ?? []), "className"],
    span: [...(defaultSchema.attributes?.span ?? []), "className"],
    h1: [...(defaultSchema.attributes?.h1 ?? []), "id"],
    h2: [...(defaultSchema.attributes?.h2 ?? []), "id"],
    h3: [...(defaultSchema.attributes?.h3 ?? []), "id"],
    h4: [...(defaultSchema.attributes?.h4 ?? []), "id"],
    h5: [...(defaultSchema.attributes?.h5 ?? []), "id"],
    h6: [...(defaultSchema.attributes?.h6 ?? []), "id"],
    a: [...(defaultSchema.attributes?.a ?? [])],
  },
  // Belt-and-suspenders: keep the default protocol allow-list (http/https/mailto/tel).
  protocols: { ...defaultSchema.protocols },
};

/**
 * Remove the leading h1 (`# Title`) from a Markdown string.
 *
 * The document viewer renders the h1 as the document header (via
 * `DocumentMeta`) so it must not also appear as the first element of the
 * rendered body — that would produce a visible duplicate title.
 *
 * Only the very first non-blank line is considered, and only if it is an h1.
 * h2+ headings, blockquotes, or paragraphs at the top are left untouched.
 * Content inside fenced code blocks is never matched.
 */
export function stripLeadingH1(markdown: string): string {
  return stripLeadingHeading(markdown, 1);
}

/**
 * Remove the leading h2 (`## Title`) from a Markdown string.
 *
 * {@link splitMarkdownSections} keeps each section's own heading, so a viewer
 * that renders one section per page and promotes that heading to the page title
 * would otherwise show it twice. Same contract as {@link stripLeadingH1}.
 */
export function stripLeadingH2(markdown: string): string {
  return stripLeadingHeading(markdown, 2);
}

/**
 * Drop a leading ATX heading of exactly `level`.
 *
 * Only the very first non-blank line is considered. A deeper or shallower
 * heading, a blockquote, or a paragraph at the top is left untouched, and
 * content inside fenced code blocks is never matched because a fence cannot be
 * the first line and also be a heading.
 */
function stripLeadingHeading(markdown: string, level: 1 | 2): string {
  const hashes = "#".repeat(level);
  const pattern = new RegExp(
    `^(?:[ \\t]*\\r?\\n)*${hashes}[ \\t]+[^\\r\\n]*(?:\\r?\\n|$)`,
  );
  return markdown.replace(pattern, "");
}

/**
 * Resolve a Markdown image `src` against `imageBase`.
 *
 * A Knowledge View document is fetched from `…/views/<view>/README.md` but
 * rendered at the SPA route `/views/<view>`, so a browser resolves relative
 * `./diagrams/…` against the route, not the file. Passing the document's own
 * directory as `imageBase` rewrites relative image URLs to absolute content
 * URLs. Absolute URLs (`http(s):`, `data:`, `blob:`, protocol-relative `//`,
 * or root-absolute `/…`) are returned unchanged.
 */
export function resolveImageSrc(
  src: string | undefined,
  imageBase?: string,
): string | undefined {
  if (!src || !imageBase) return src;
  if (/^(?:[a-z]+:|\/\/|\/)/i.test(src)) return src;
  const cleaned = src.replace(/^\.\//, "");
  return `${imageBase.replace(/\/+$/, "")}/${cleaned.replace(/^\/+/, "")}`;
}

export interface TocEntry {
  readonly id: string;
  readonly text: string;
  readonly depth: 2 | 3;
}

/**
 * Extract a lightweight table of contents from raw Markdown.
 *
 * Heuristic: only `##` and `###` headings, slugified to match
 * `rehype-slug`'s output (lowercase, non-word -> '-'). The TOC is purely a
 * navigation aid; the rendered document remains the source of truth.
 */
export function extractToc(markdown: string): TocEntry[] {
  const lines = markdown.split(/\r?\n/);
  const entries: TocEntry[] = [];
  let inCodeBlock = false;
  for (const line of lines) {
    if (/^\s*```/.test(line)) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;
    const match = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match) continue;
    const depth = match[1]!.length === 2 ? 2 : 3;
    const text = match[2]!.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").trim();
    if (!text) continue;
    entries.push({ id: slugify(text), text, depth });
  }
  return entries;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[`*_~]/g, "")
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export interface MarkdownSection {
  /** Slugified heading text (matches {@link extractToc} / `rehype-slug`). */
  readonly slug: string;
  /** The `##` heading text. */
  readonly title: string;
  /** The section body **including** its own `## heading` line. */
  readonly markdown: string;
}

export interface SplitDocument {
  /** Everything before the first `##` heading (intro, disclaimer, links). */
  readonly preamble: string;
  readonly sections: readonly MarkdownSection[];
}

/**
 * Split a composed Knowledge-View document into its top-level (`##`) sections.
 *
 * Used to render one section per page instead of one long scroll. Splitting is
 * done deterministically on `##` headings that are **not** inside fenced code
 * blocks; each section keeps its own heading so it renders with a visible title
 * and a stable anchor. Content before the first `##` heading is returned as the
 * `preamble` (the viewer can show or drop it). A document with no `##` heading
 * yields an empty `sections` list and the whole input as `preamble`.
 */
export function splitMarkdownSections(markdown: string): SplitDocument {
  const lines = markdown.split(/\r?\n/);
  const preambleLines: string[] = [];
  const sections: { title: string; lines: string[] }[] = [];
  let inCodeBlock = false;

  for (const line of lines) {
    if (/^\s*```/.test(line)) inCodeBlock = !inCodeBlock;
    const heading = inCodeBlock ? null : /^##\s+(.+?)\s*#*\s*$/.exec(line);
    if (heading) {
      const title = heading[1]!.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").trim();
      sections.push({ title, lines: [line] });
    } else if (sections.length === 0) {
      preambleLines.push(line);
    } else {
      sections[sections.length - 1]!.lines.push(line);
    }
  }

  return {
    preamble: preambleLines.join("\n").trim(),
    sections: sections.map((s) => ({
      slug: slugify(s.title),
      title: s.title,
      markdown: s.lines.join("\n").trim(),
    })),
  };
}

/** Slugify a heading the same way the TOC / section splitter does. */
export function sectionSlug(title: string): string {
  return slugify(title);
}
